import type { ReactNode } from 'react';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { QText } from '@captivateiq/quotient';

import type { ConversationListItem } from '@captivateiq/assistant/types';
import { ConversationRow } from './ConversationRow';
import { groupConversationsByDate } from './groupConversationsByDate';

type GroupedConversationListProps = {
  /** Already filtered by the caller (e.g. by search query) — this component only groups/renders. */
  conversations: ConversationListItem[];
  emptyState: ReactNode;
  /** Called after a row's conversation is successfully opened, e.g. to close a containing dropdown. */
  onSelect?: () => void;
};

export const GroupedConversationList = ({ conversations, emptyState, onSelect }: GroupedConversationListProps) => {
  const { t } = useTranslation('assistant');
  const groups = useMemo(() => groupConversationsByDate(conversations), [conversations]);

  if (!conversations.length) {
    return <>{emptyState}</>;
  }

  return (
    <>
      {groups.map((group, index) => (
        <React.Fragment key={group.key}>
          <QText
            sx={{
              fontSize: '11px',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              color: 'text.onSurface.background.mediumContrast',
              padding: '4px 12px 2px',
              borderTop: index === 0 ? 'none' : '1px solid',
              borderTopColor: 'border.neutralPrimary.mediumContrast',
              paddingTop: index === 0 ? '4px' : '14px',
            }}
          >
            {t(`history_sections.${group.key}`)}
          </QText>
          {group.conversations.map((conversation) => (
            <ConversationRow
              id={conversation.id}
              key={conversation.id}
              lastUpdated={conversation.updated_at}
              title={conversation.name}
              onSelect={onSelect}
            />
          ))}
        </React.Fragment>
      ))}
    </>
  );
};
